import { modelSupportsTemperature, type Agent, type Run } from '@agents/shared';
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';
import { useLayoutEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Icon } from '../components/icons';
import { useCan } from '../components/shell';
import {
  AgentAvatar,
  AgentStatusPill,
  Alert,
  BackLink,
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  OverflowMenu,
  PageHeader,
  problemMessage,
  RunStatusPill,
  SkeletonRows,
  TableSkeleton,
  useToast,
  type MenuAction,
} from '../components/ui';
import { useDocumentTitle } from '../lib/document-title';
import { dateTime, duration, relativeTime, truncate } from '../lib/format';
import {
  useAgent,
  useCreateRun,
  useKnowledgeSources,
  usePublishAgent,
  useRuns,
  useSaveAgent,
  useTools,
} from '../lib/queries';

const route = getRouteApi('/_app/agentes/$agentId');

function RunComposer({ agent, primary }: { agent: Agent; primary: boolean }) {
  const navigate = useNavigate();
  const createRun = useCreateRun(agent.id);
  const [input, setInput] = useState('');
  const [error, setError] = useState<string>();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!input.trim()) {
      setError('Escribe qué necesitas que haga el agente.');
      return;
    }
    setError(undefined);
    try {
      const run = await createRun.mutateAsync(input.trim());
      await navigate({ to: '/ejecuciones/$runId', params: { runId: run.id } });
    } catch (err) {
      setError(problemMessage(err, 'No se pudo iniciar la ejecución. Inténtalo de nuevo.'));
    }
  };

  return (
    <form className="composer" data-invalid={!!error || undefined} onSubmit={submit} noValidate>
      <div className="composer-head">
        <label htmlFor="run-input">Ejecutar el agente</label>
        <span className="chip" title="Versión que se ejecutará">
          v{agent.currentVersion} publicada
        </span>
      </div>
      <textarea
        id="run-input"
        rows={4}
        maxLength={10_000}
        placeholder="Ej.: Un cliente compró hace 20 días y quiere devolver un producto abierto. ¿Qué le respondemos?"
        value={input}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? 'run-input-error' : undefined}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void submit(e);
        }}
      />
      {error && (
        <p className="composer-error" id="run-input-error" role="alert">
          {error}
        </p>
      )}
      <div className="composer-foot">
        <span className="hint">
          <span className="kbd">Ctrl/⌘</span>
          <span className="kbd">↵</span> para ejecutar
        </span>
        <Button
          variant={primary ? 'primary' : 'secondary'}
          type="submit"
          icon="play"
          loading={createRun.isPending}
        >
          Ejecutar
        </Button>
      </div>
    </form>
  );
}

function Blocked({ title, children }: { title: string; children: string }) {
  return (
    <div className="blocked">
      <span className="tool-icon">
        <Icon name="lock" />
      </span>
      <div>
        <strong>{title}</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

function RunHistory({ runs }: { runs: Run[] }) {
  const navigate = useNavigate();
  if (!runs.length) {
    return (
      <EmptyState compact title="Sin ejecuciones todavía">
        Cada ejecución de este agente aparecerá aquí con su estado y su resultado.
      </EmptyState>
    );
  }
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th scope="col">Estado</th>
            <th scope="col">Solicitud</th>
            <th scope="col" className="num" data-optional>
              Duración
            </th>
            <th scope="col" data-optional>
              Cuándo
            </th>
          </tr>
        </thead>
        <tbody>
          {runs.map((r) => (
            <tr
              key={r.id}
              data-href
              onClick={(e) => {
                if ((e.target as HTMLElement).closest('a')) return;
                void navigate({ to: '/ejecuciones/$runId', params: { runId: r.id } });
              }}
            >
              <td>
                <RunStatusPill status={r.status} />
              </td>
              <td data-mobile-line>
                <Link to="/ejecuciones/$runId" params={{ runId: r.id }} className="cell-clip row-link">
                  {truncate(r.input, 72)}
                </Link>
              </td>
              <td className="num mono" data-optional>
                {duration(r.durationMs)}
              </td>
              <td className="muted nowrap" data-optional title={dateTime(r.createdAt)}>
                {relativeTime(r.createdAt)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Instructions({ text }: { text: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [clamped, setClamped] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el) setClamped(el.scrollHeight > el.clientHeight + 1);
  }, [text]);
  return (
    <>
      <div
        ref={ref}
        id="agent-instructions"
        className="instructions"
        data-clamped={clamped || undefined}
        data-expanded={expanded || undefined}
      >
        {text}
      </div>
      {(clamped || expanded) && (
        <Button
          variant="link"
          aria-controls="agent-instructions"
          aria-expanded={expanded}
          style={{ fontSize: 13, marginTop: 8 }}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Ver menos' : 'Ver todo'}
        </Button>
      )}
    </>
  );
}

function AgentAside({ agent }: { agent: Agent }) {
  const tools = useTools();
  const byId = new Map(tools.data?.map((t) => [t.id, t]));
  const can = useCan();
  const sources = useKnowledgeSources(can('knowledge.read'));
  const sourceById = new Map(sources.data?.map((s) => [s.id, s]));
  const shared = sources.data?.filter((s) => s.visibility === 'tenant') ?? [];
  return (
    <aside className="aside">
      <section className="aside-block">
        <h2>Configuración</h2>
        <dl className="facts">
          <dt>Versión publicada</dt>
          <dd className="mono">{agent.currentVersion ? `v${agent.currentVersion}` : 'Ninguna'}</dd>
          <dt>Publicada</dt>
          <dd title={agent.publishedAt ? dateTime(agent.publishedAt) : undefined}>
            {agent.publishedAt ? relativeTime(agent.publishedAt) : '—'}
          </dd>
          <dt>Modelo</dt>
          <dd className="mono">{agent.config.model}</dd>
          <dt>Temperatura</dt>
          <dd className="mono">
            {modelSupportsTemperature(agent.config.model) ? agent.config.temperature : 'No aplica'}
          </dd>
          <dt>Límite</dt>
          <dd>{agent.config.maxToolCalls} herramientas por ejecución</dd>
          <dt>Datos personales</dt>
          <dd>
            {agent.config.dataPolicy?.redactPersonalData ? 'Se ocultan al modelo' : 'Se envían al modelo'}
          </dd>
          <dt>Actualizado</dt>
          <dd title={dateTime(agent.updatedAt)}>{relativeTime(agent.updatedAt)}</dd>
          <dt>Identificador</dt>
          <dd className="mono muted">{agent.id}</dd>
        </dl>
      </section>
      <section className="aside-block">
        <h2>
          Herramientas{' '}
          <span className="mono muted" style={{ fontWeight: 400 }}>
            {agent.toolIds.length}
          </span>
        </h2>
        {agent.toolIds.length ? (
          <ul className="tool-list">
            {agent.toolIds.map((id) => {
              const t = byId.get(id);
              return (
                <li key={id}>
                  <Icon name={t?.kind === 'action' ? 'zap' : 'eye'} />
                  {t?.name ?? id}
                  <span className="chip">{id}</span>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>
            Sin herramientas. Solo responde con sus instrucciones.
          </p>
        )}
      </section>
      {can('knowledge.read') && (
        <section className="aside-block">
          <h2>
            Conocimiento{' '}
            <span className="mono muted" style={{ fontWeight: 400 }}>
              {agent.knowledgeSourceIds.length + shared.length}
            </span>
          </h2>
          {agent.knowledgeSourceIds.length + shared.length ? (
            <ul className="tool-list">
              {agent.knowledgeSourceIds.map((id) => (
                <li key={id}>
                  <Icon name="book" />
                  <Link to="/conocimiento/$sourceId" params={{ sourceId: id }}>
                    {sourceById.get(id)?.name ?? 'Fuente'}
                  </Link>
                </li>
              ))}
              {shared.map((s) => (
                <li key={s.id}>
                  <Icon name="book" />
                  {s.name}
                  <span className="chip">Compartida</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted" style={{ margin: 0, fontSize: 13 }}>
              Sin fuentes de conocimiento asignadas.
            </p>
          )}
        </section>
      )}
      <section className="aside-block">
        <h2>Instrucciones</h2>
        <Instructions text={agent.instructions} />
      </section>
    </aside>
  );
}

export function AgentDetailPage() {
  const { agentId } = route.useParams();
  const agent = useAgent(agentId);
  const runs = useRuns(agentId);
  const publish = usePublishAgent(agentId);
  const save = useSaveAgent();
  const toast = useToast();
  const can = useCan();
  const [confirmPause, setConfirmPause] = useState(false);
  useDocumentTitle(agent.data?.name ?? 'Agente');

  const back = <BackLink to="/agentes">Agentes</BackLink>;
  if (agent.isPending) {
    return (
      <>
        {back}
        <div className="skeleton" style={{ width: 260, height: 28, marginBottom: 28 }} />
        <SkeletonRows rows={6} />
      </>
    );
  }
  if (agent.isError) {
    return (
      <>
        {back}
        <ErrorState error={agent.error} onRetry={() => void agent.refetch()} />
      </>
    );
  }
  const a = agent.data;
  const canPublish = can('agent.publish');
  const canEdit = can('agent.edit');
  const nextVersion = (a.currentVersion ?? 0) + 1;
  const isDraft = a.currentVersion === null;
  const runnable = a.status === 'active' && !isDraft;

  const doPublish = async () => {
    try {
      const updated = await publish.mutateAsync();
      toast(`Versión v${updated.currentVersion} publicada`, {
        detail: 'Las próximas ejecuciones usarán esta versión.',
      });
    } catch (err) {
      toast('No se pudo publicar', { tone: 'danger', detail: problemMessage(err, 'Inténtalo de nuevo.') });
    }
  };

  const setStatus = async (status: 'active' | 'paused') => {
    try {
      await save.mutateAsync({ id: a.id, input: { status } });
      toast(status === 'paused' ? 'Agente pausado' : 'Agente reactivado');
    } catch (err) {
      toast('No se pudo cambiar el estado', {
        tone: 'danger',
        detail: problemMessage(err, 'Inténtalo de nuevo.'),
      });
    } finally {
      setConfirmPause(false);
    }
  };

  const copyId = async () => {
    try {
      await navigator.clipboard.writeText(a.id);
      toast('Identificador copiado');
    } catch {
      toast('No se pudo copiar', { tone: 'danger', detail: a.id });
    }
  };

  // One banner per state, carrying its own action. When it exists, its button is the view's
  // single primary action; otherwise the primary action is "Ejecutar".
  let banner: ReactNode = null;
  if (isDraft) {
    banner = (
      <Alert
        className="page-alert"
        title="Este agente es un borrador"
        actions={
          canPublish && (
            <Button variant="primary" loading={publish.isPending} onClick={() => void doPublish()}>
              Publicar v1
            </Button>
          )
        }
      >
        <p>
          {canPublish
            ? 'Publícalo para crear la versión v1 y empezar a ejecutarlo.'
            : 'Un administrador debe publicarlo antes de que puedas ejecutarlo.'}
        </p>
      </Alert>
    );
  } else if (a.status === 'paused') {
    banner = (
      <Alert
        className="page-alert"
        tone="warning"
        icon="pause"
        title="Agente pausado"
        actions={
          canEdit && (
            <Button variant="primary" loading={save.isPending} onClick={() => void setStatus('active')}>
              Reactivar
            </Button>
          )
        }
      >
        <p>Nadie puede iniciar ejecuciones nuevas hasta que lo reactives.</p>
      </Alert>
    );
  } else if (a.hasUnpublishedChanges) {
    banner = (
      <Alert
        className="page-alert"
        title="Hay cambios sin publicar"
        actions={
          canPublish && (
            <Button variant="primary" loading={publish.isPending} onClick={() => void doPublish()}>
              Publicar v{nextVersion}
            </Button>
          )
        }
      >
        <p>
          Las ejecuciones siguen usando v{a.currentVersion}
          {canPublish
            ? ` hasta que publiques v${nextVersion}.`
            : ' hasta que un administrador publique los cambios.'}
        </p>
      </Alert>
    );
  }
  const bannerHasPrimary = (isDraft || a.hasUnpublishedChanges) && canPublish;

  const menuActions: MenuAction[] = [];
  if (canEdit && a.status === 'active' && !isDraft) {
    menuActions.push({ label: 'Pausar agente', icon: 'pause', onSelect: () => setConfirmPause(true) });
  }
  if (canEdit && a.status === 'paused') {
    menuActions.push({ label: 'Reactivar agente', icon: 'play', onSelect: () => void setStatus('active') });
  }
  menuActions.push({ label: 'Copiar identificador', icon: 'hash', onSelect: () => void copyId() });

  return (
    <>
      <PageHeader
        back={back}
        lead={<AgentAvatar id={a.id} name={a.name} size="lg" />}
        title={a.name}
        meta={
          <>
            <AgentStatusPill status={a.status} />
            {a.description && <span>{a.description}</span>}
          </>
        }
        actions={
          <>
            {canEdit && (
              <Link to="/agentes/$agentId/editar" params={{ agentId: a.id }} className="btn">
                <Icon name="edit" />
                Editar
              </Link>
            )}
            <OverflowMenu label="Más acciones" actions={menuActions} />
          </>
        }
      />

      {banner}

      <div className="split">
        <div className="stack">
          {can('run.create') &&
            (runnable ? (
              <RunComposer agent={a} primary={!bannerHasPrimary} />
            ) : isDraft ? (
              <Blocked title="Disponible cuando publiques el agente">
                La primera publicación crea una versión estable. Así cada ejecución queda asociada a una
                configuración concreta.
              </Blocked>
            ) : (
              <Blocked title="Ejecución en pausa">Reactiva el agente para volver a ejecutarlo.</Blocked>
            ))}
          <section>
            <div className="section-head">
              <h2 className="section-title">Ejecuciones recientes</h2>
              {!!runs.data?.length && (
                <Link to="/ejecuciones" className="btn-link" style={{ fontSize: 13 }}>
                  Ver todas
                </Link>
              )}
            </div>
            {runs.isPending && <TableSkeleton rows={3} cols={4} />}
            {runs.isError && <ErrorState error={runs.error} onRetry={() => void runs.refetch()} />}
            {runs.data && <RunHistory runs={runs.data} />}
          </section>
        </div>
        <AgentAside agent={a} />
      </div>

      <ConfirmDialog
        open={confirmPause}
        onOpenChange={setConfirmPause}
        title={`¿Pausar ${a.name}?`}
        description="Nadie podrá iniciar nuevas ejecuciones hasta que lo reactives. Las ejecuciones en curso terminarán normalmente."
        confirmLabel="Pausar agente"
        onConfirm={() => void setStatus('paused')}
        loading={save.isPending}
      />
    </>
  );
}
